import { reports } from '@/API';
import { type OrderForWorklist, type WorklistOrderReportInfo } from '@/Models/GenericWorklist/WorklistResult';
import { makeAutoObservable } from 'mobx';

interface ReportMetadata {
    selectedReportId?: number;
    dictatedBy: string;
    dictatedDateTime: string;
    validatedBy: string;
    validatedDateTime: string;
    hasValidationData: boolean;
}

export interface IReportPopupStore {
    isLoadingReportContent: boolean;
    reportContent: string;
    setSelectionContext(
        selectedOrder: OrderForWorklist | undefined,
        selectedReportId: number | undefined,
        selectedReportProcedureIds: number[],
    ): void;
    readonly selectedReportInfo: WorklistOrderReportInfo | null;
    readonly proceduresForSelectedReport: OrderForWorklist['OrderProceduresInfos'];
    readonly patientDisplayName: string;
    readonly reportMetadata: ReportMetadata;
    loadReportContent(reportId: number): Promise<void>;
    reset(): void;
}

export default class ReportPopupStore implements IReportPopupStore {
    isLoadingReportContent = false;
    reportContent = '';
    selectedOrder?: OrderForWorklist;
    selectedReportId?: number;
    selectedReportProcedureIds: number[] = [];

    constructor() {
        makeAutoObservable(this);
    }

    loadReportContent = async (reportId: number): Promise<void> => {
        this.setLoadingReportContent(true);

        try {
            const response = await reports.getContentByReportId(reportId);
            this.setReportContent(response?.Content ?? '');
        }
        catch (error) {
            console.error('Failed to load report content:', error);
            this.setReportContent('');
        }
        finally {
            this.setLoadingReportContent(false);
        }
    };

    reset = (): void => {
        this.reportContent = '';
        this.isLoadingReportContent = false;
    };

    setSelectionContext = (
        selectedOrder: OrderForWorklist | undefined,
        selectedReportId: number | undefined,
        selectedReportProcedureIds: number[],
    ): void => {
        this.selectedOrder = selectedOrder;
        this.selectedReportId = selectedReportId;
        this.selectedReportProcedureIds = selectedReportProcedureIds;
    };

    get selectedReportInfo(): WorklistOrderReportInfo | null {
        const reportInfos = this.selectedOrder?.OrderReportsInfos ?? [];

        if (!this.selectedReportId) {
            return null;
        }

        return reportInfos.find((reportInfo) => reportInfo.ReportID === this.selectedReportId) ?? null;
    }

    get proceduresForSelectedReport(): NonNullable<OrderForWorklist['OrderProceduresInfos']> {
        if (!this.selectedOrder) {
            return [];
        }

        if (
            this.selectedReportInfo?.IsOrderTypeReport
            && this.selectedReportProcedureIds.length === 0
        ) {
            return this.selectedOrder.OrderProceduresInfos ?? [];
        }

        return (this.selectedOrder.OrderProceduresInfos ?? []).filter((orderProcedure) => (
            this.selectedReportProcedureIds.includes(orderProcedure.OrderProcedureID)
        ));
    }

    get patientDisplayName(): string {
        if (!this.selectedOrder) {
            return '';
        }

        const firstName = this.selectedOrder.PatientInfo.FirstName ?? '';
        const lastName = this.selectedOrder.PatientInfo.LastName;
        if (firstName === '') {
            return lastName;
        }

        return `${firstName} ${lastName}`;
    }

    get reportMetadata(): ReportMetadata {
        const dictatedBy = this.selectedReportInfo?.ReportCreatedBy ?? '-';
        const dictatedDateTime = this.formatDateTime(this.selectedReportInfo?.ReportCreatedDateTime);
        const validatedBy = this.selectedReportInfo?.ReportValidatedBy ?? '-';
        const validatedDateTime = this.formatDateTime(this.selectedReportInfo?.ReportValidatedDateTime);
        const hasValidationData = validatedBy !== '-' && validatedDateTime !== '-';

        return {
            selectedReportId: this.selectedReportInfo?.ReportID,
            dictatedBy,
            dictatedDateTime,
            validatedBy,
            validatedDateTime,
            hasValidationData,
        };
    }

    private readonly formatDateTime = (date: Date | null | undefined): string => {
        if (!date) {
            return '-';
        }

        return new Date(date).toLocaleString('en-GB');
    };

    private readonly setLoadingReportContent = (isLoading: boolean): void => {
        this.isLoadingReportContent = isLoading;
    };

    private readonly setReportContent = (content: string): void => {
        this.reportContent = content;
    };
}
